import { useMemo, useState, useEffect } from 'react';
import { useList, useCustom, useApiUrl } from '@refinedev/core';
import { formatDate } from '@/lib/utils';
import { toast } from '@/hooks/useToast';
import { adminFetch, AdminApiError } from '@/providers/admin-api';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { SortableHeader, type SortOrder } from '@/components/table/SortableHeader';
import { BetaDetailDrawer, StatusBadge, type BetaApp } from './BetaDetailDrawer';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  CheckCircle,
  XCircle,
  Send,
  X,
  Filter,
  Users,
  Sparkles,
  Rocket,
} from 'lucide-react';

interface BetaStats {
  total: number;
  by_status: { pending: number; approved: number; rejected: number; invited: number };
  approval_rate: number;
  invited_rate: number;
  by_channel: { channel: string; count: number }[];
  viral: { avg_friends_to_invite: number; total_potential_invitees: number };
  joined: number;
  joined_rate: number;
  total_app_users: number;
}

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const STATUS_OPTIONS: ('all' | BetaApp['application_status'])[] = [
  'all',
  'pending',
  'approved',
  'rejected',
  'invited',
];

const SORTABLE_FIELDS = ['created_at', 'friends_to_invite'] as const;
type SortField = (typeof SORTABLE_FIELDS)[number];

function pct(v: number): string {
  return `${Math.round((v || 0) * 100)}%`;
}

export function BetaApplicationsPage() {
  const apiUrl = useApiUrl();
  const { hasPermission } = useAdminPermissions();
  const canReview = hasPermission('growth.beta.review');
  const canInvite = hasPermission('growth.beta.invite');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | BetaApp['application_status']>('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [drawerApp, setDrawerApp] = useState<BetaApp | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  // Reject reason modal
  const [rejectTarget, setRejectTarget] = useState<BetaApp | null>(null);
  const [bulkRejecting, setBulkRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const { data: statsData, refetch: refetchStats } = useCustom<BetaStats>({
    url: '/v1/admin/beta-applications/stats',
    method: 'get',
  });
  const stats = statsData?.data;

  const { data, isLoading, isError, refetch } = useList<BetaApp>({
    resource: 'beta-applications',
    pagination: { current: currentPage, pageSize },
    filters: [
      ...(search ? [{ field: 'search', operator: 'contains' as const, value: search }] : []),
      ...(statusFilter !== 'all'
        ? [{ field: 'status', operator: 'eq' as const, value: statusFilter }]
        : []),
      ...(channelFilter !== 'all'
        ? [{ field: 'channel', operator: 'eq' as const, value: channelFilter }]
        : []),
    ],
    sorters: [{ field: sortField, order: sortOrder }],
  });

  const entries = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  // Keep the open drawer in sync with refetched rows.
  const drawerCurrent = useMemo(
    () => (drawerApp ? entries.find((e) => e.id === drawerApp.id) ?? drawerApp : null),
    [drawerApp, entries],
  );

  useEffect(() => {
    setSelected(new Set());
  }, [currentPage, pageSize, search, statusFilter, channelFilter, sortField, sortOrder]);

  const refreshAll = async () => {
    await Promise.all([refetch(), refetchStats()]);
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortField(field as SortField);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  // ---- Single-row actions ----
  const doApprove = async (app: BetaApp) => {
    setActionBusy(true);
    try {
      await adminFetch(`${apiUrl}/v1/admin/beta-applications/${app.id}/approve`, {
        method: 'POST',
      });
      toast({ title: 'Approved', description: `${app.email} approved for beta.` });
      await refreshAll();
    } catch (e: any) {
      toast({
        title: 'Approve failed',
        description: e?.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setActionBusy(false);
    }
  };

  const doReject = async (app: BetaApp, reason: string) => {
    setActionBusy(true);
    try {
      await adminFetch(`${apiUrl}/v1/admin/beta-applications/${app.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason || undefined }),
      });
      toast({ title: 'Rejected', description: `${app.email} rejected.` });
      await refreshAll();
    } catch (e: any) {
      toast({
        title: 'Reject failed',
        description: e?.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setActionBusy(false);
    }
  };

  const errorCode = (e: unknown): string | undefined => {
    if (e instanceof AdminApiError && e.body && typeof e.body === 'object') {
      const body = e.body as Record<string, unknown>;
      const code = body.code ?? body.error ?? body.title ?? body.message
        ?? (typeof body.type === 'string' ? body.type.split('/').pop() : undefined);
      if (typeof code === 'string') return code;
    }
    return undefined;
  };

  const doSendLinks = async (app: BetaApp) => {
    setActionBusy(true);
    try {
      await adminFetch(`${apiUrl}/v1/admin/beta-applications/${app.id}/send-links`, {
        method: 'POST',
      });
      toast({
        title: 'Links sent',
        description: `Invite links emailed to ${app.email}.`,
      });
      await refreshAll();
    } catch (e: any) {
      const code = errorCode(e);
      if (code === 'no_links_configured') {
        toast({
          title: 'No links configured',
          description: 'Set the beta invite links in Settings before sending.',
          variant: 'destructive',
        });
      } else if (code === 'email_delivery_failed') {
        toast({
          title: 'Email delivery failed',
          description: `Could not deliver links to ${app.email}. Try again later.`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Send links failed',
          description: e?.message || 'Unknown error',
          variant: 'destructive',
        });
      }
    } finally {
      setActionBusy(false);
    }
  };

  // ---- Bulk actions ----
  const runBulk = async (action: 'approve' | 'reject' | 'send-links', reason?: string) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setActionBusy(true);
    try {
      const res = await adminFetch(`${apiUrl}/v1/admin/beta-applications/bulk`, {
        method: 'POST',
        body: JSON.stringify({ action, ids, reason: reason || undefined }),
      });
      const payload = (await res.json()) as { results: { id: string; ok: boolean }[] };
      const ok = payload.results.filter((r) => r.ok).length;
      const failed = payload.results.length - ok;
      toast({
        title: `Bulk ${action}`,
        description: `${ok} succeeded${failed ? `, ${failed} failed` : ''}.`,
        variant: failed ? 'warning' : 'success',
      });
      setSelected(new Set());
      await refreshAll();
    } catch (e: any) {
      toast({
        title: `Bulk ${action} failed`,
        description: e?.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setActionBusy(false);
    }
  };

  // ---- CSV export (client-side from fetched rows) ----
  const handleExport = () => {
    try {
      const cols: { key: keyof BetaApp; label: string }[] = [
        { key: 'first_name', label: 'First Name' },
        { key: 'last_name', label: 'Last Name' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'location', label: 'City' },
        { key: 'relationship_status', label: 'Relationship Status' },
        { key: 'application_status', label: 'Status' },
        { key: 'friends_to_invite', label: 'Friends To Invite' },
        { key: 'why_early_access', label: 'Why Early Access' },
        { key: 'channel', label: 'Channel' },
        { key: 'utm_source', label: 'UTM Source' },
        { key: 'utm_medium', label: 'UTM Medium' },
        { key: 'utm_campaign', label: 'UTM Campaign' },
        { key: 'gclid', label: 'gclid' },
        { key: 'fbclid', label: 'fbclid' },
        { key: 'ttclid', label: 'ttclid' },
        { key: 'created_at', label: 'Applied' },
        { key: 'reviewed_at', label: 'Reviewed' },
        { key: 'links_sent_at', label: 'Links Sent' },
        { key: 'rejection_reason', label: 'Rejection Reason' },
      ];
      const esc = (v: unknown) => {
        const s = v == null ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const header = cols.map((c) => c.label).join(',');
      const rows = entries.map((row) => cols.map((c) => esc(row[c.key])).join(','));
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `beta-applications-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: 'Export Complete', description: 'Beta applications exported to CSV' });
    } catch {
      toast({
        title: 'Export Failed',
        description: 'Could not export beta applications',
        variant: 'destructive',
      });
    }
  };

  const toggleSelectAll = () => {
    if (selected.size === entries.length) setSelected(new Set());
    else setSelected(new Set(entries.map((e) => e.id)));
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const openReject = (app: BetaApp) => {
    setRejectTarget(app);
    setBulkRejecting(false);
    setRejectReason('');
  };

  const confirmReject = async () => {
    if (bulkRejecting) {
      await runBulk('reject', rejectReason);
    } else if (rejectTarget) {
      await doReject(rejectTarget, rejectReason);
    }
    setRejectTarget(null);
    setBulkRejecting(false);
    setRejectReason('');
  };

  if (isError) {
    return (
      <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
        Failed to load beta applications. Please try again.
      </div>
    );
  }

  const approvedTotal = stats ? stats.by_status.approved + stats.by_status.invited : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Beta Applications</h2>
          <p className="text-muted-foreground">
            Applicants via the /beta form — triage, invite, and track conversion to signup
          </p>
          {stats && (
            <p className="mt-1 text-xs text-muted-foreground">
              {stats.total_app_users} people have signed up to test the app — most join directly
              via store links (Members → Users), not this form.
            </p>
          )}
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {/* CMO stat cards */}
      {stats && (
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Funnel card */}
          <div className="rounded-lg border bg-card p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <Rocket className="h-4 w-4 text-primary" />
              Funnel
            </div>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Applied</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <ChevronRight className="mb-1 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold">{approvedTotal}</p>
                <p className="text-xs text-muted-foreground">{pct(stats.approval_rate)} rate</p>
              </div>
              <ChevronRight className="mb-1 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Invited</p>
                <p className="text-2xl font-bold">{stats.by_status.invited}</p>
                <p className="text-xs text-muted-foreground">{pct(stats.invited_rate)} rate</p>
              </div>
              <ChevronRight className="mb-1 h-4 w-4 text-muted-foreground" />
              <div
                title="Applicants who signed up in the app, matched by email. Lower bound — anyone who signed up with a different email (e.g. Apple private relay) isn't counted."
              >
                <p className="text-xs text-muted-foreground">Joined</p>
                <p className="text-2xl font-bold">{stats.joined}</p>
                <p className="text-xs text-muted-foreground">{pct(stats.joined_rate)} rate</p>
              </div>
            </div>
          </div>

          {/* Referral intent card */}
          <div className="rounded-lg border bg-card p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <Sparkles className="h-4 w-4 text-purple-600" />
              Referral Intent
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Avg friends / applicant</p>
                <p className="text-2xl font-bold">
                  {stats.viral.avg_friends_to_invite.toFixed(1)}
                </p>
                <p className="text-xs text-muted-foreground">
                  from {stats.total} application{stats.total === 1 ? '' : 's'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Potential invitees</p>
                <p className="text-2xl font-bold">{stats.viral.total_potential_invitees}</p>
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Self-reported on the form — referral intent, not a measured viral coefficient (which
              needs invite→signup tracking).
            </p>
          </div>

          {/* Top channels card */}
          <div className="rounded-lg border bg-card p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <Users className="h-4 w-4 text-blue-600" />
              Top Channels
            </div>
            {stats.by_channel.length === 0 ? (
              <p className="text-sm text-muted-foreground">No channel data yet</p>
            ) : (
              <ul className="space-y-1">
                {stats.by_channel.slice(0, 5).map((c) => (
                  <li
                    key={c.channel || 'unknown'}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="truncate text-muted-foreground">
                      {c.channel || 'unknown'}
                    </span>
                    <span className="font-medium">{c.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setCurrentPage(1);
          }}
          className="flex gap-2"
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by email or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 w-72 rounded-lg border bg-background pl-10 pr-8 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setCurrentPage(1);
                }}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <button
            type="submit"
            className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Search
          </button>
        </form>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as typeof statusFilter);
                setCurrentPage(1);
              }}
              aria-label="Filter by status"
              className="h-10 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s} className="capitalize">
                  {s === 'all' ? 'All statuses' : s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Channel:</label>
            <select
              value={channelFilter}
              onChange={(e) => {
                setChannelFilter(e.target.value);
                setCurrentPage(1);
              }}
              aria-label="Filter by channel"
              className="h-10 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="all">All channels</option>
              {(stats?.by_channel ?? []).map((c) => (
                <option key={c.channel || 'unknown'} value={c.channel || ''}>
                  {(c.channel || 'unknown') + ` (${c.count})`}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Show:</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              aria-label="Items per page"
              className="h-10 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (canReview || canInvite) && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/40 px-4 py-3">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <div className="flex flex-wrap items-center gap-2">
            {canReview && (
              <>
                <button
                  onClick={() => runBulk('approve')}
                  disabled={actionBusy}
                  className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  <CheckCircle className="h-4 w-4" />
                  Approve
                </button>
                <button
                  onClick={() => {
                    setBulkRejecting(true);
                    setRejectTarget(null);
                    setRejectReason('');
                  }}
                  disabled={actionBusy}
                  className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
                >
                  <XCircle className="h-4 w-4" />
                  Reject
                </button>
              </>
            )}
            {canInvite && (
              <button
                onClick={() => runBulk('send-links')}
                disabled={actionBusy}
                className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                Send Links
              </button>
            )}
          </div>
          <button
            onClick={() => setSelected(new Set())}
            className="ml-auto text-sm text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={entries.length > 0 && selected.size === entries.length}
                    onChange={toggleSelectAll}
                    aria-label="Select all"
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  City
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  Status
                </th>
                <SortableHeader
                  field="friends_to_invite"
                  label="Friends"
                  activeField={sortField}
                  activeOrder={sortOrder}
                  onSort={handleSort}
                />
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  Channel
                </th>
                <SortableHeader
                  field="created_at"
                  label="Applied"
                  activeField={sortField}
                  activeOrder={sortOrder}
                  onSort={handleSort}
                />
                <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: pageSize }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="animate-pulse border-b last:border-0">
                    {Array.from({ length: 9 }).map((__, ci) => (
                      <td key={ci} className="px-4 py-3">
                        <div className="h-4 w-20 rounded bg-muted" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                    {search || statusFilter !== 'all' || channelFilter !== 'all'
                      ? 'No matching applications found'
                      : 'No beta applications yet'}
                  </td>
                </tr>
              ) : (
                entries.map((app) => {
                  const fullName =
                    `${app.first_name ?? ''} ${app.last_name ?? ''}`.trim() || '—';
                  return (
                    <tr
                      key={app.id}
                      onClick={() => setDrawerApp(app)}
                      className={`cursor-pointer border-b last:border-0 hover:bg-muted/30 ${
                        selected.has(app.id) ? 'bg-primary/5' : ''
                      }`}
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(app.id)}
                          onChange={() => toggleSelect(app.id)}
                          aria-label={`Select ${app.email}`}
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                      </td>
                      <td className="px-4 py-3 font-medium">{fullName}</td>
                      <td className="px-4 py-3 text-sm">{app.email}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {app.location || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <StatusBadge status={app.application_status} />
                          {app.joined && (
                            <span
                              title="This applicant has signed up as a real app user"
                              className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700"
                            >
                              <CheckCircle className="h-3 w-3" />
                              Joined
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{app.friends_to_invite ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {app.channel || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatDate(app.created_at)}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {canReview && app.application_status === 'pending' && (
                            <>
                              <button
                                onClick={() => doApprove(app)}
                                disabled={actionBusy}
                                title="Approve"
                                className="rounded-lg p-2 text-muted-foreground hover:bg-primary/10 hover:text-primary disabled:opacity-50"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => openReject(app)}
                                disabled={actionBusy}
                                title="Reject"
                                className="rounded-lg p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {canInvite &&
                            (app.application_status === 'approved' ||
                              app.application_status === 'invited') && (
                              <button
                                onClick={() => doSendLinks(app)}
                                disabled={actionBusy}
                                title={
                                  app.application_status === 'invited'
                                    ? 'Resend Links'
                                    : 'Send Links'
                                }
                                className="flex items-center gap-1 rounded-lg p-2 text-muted-foreground hover:bg-green-100 hover:text-green-700 disabled:opacity-50"
                              >
                                <Send className="h-4 w-4" />
                                <span className="text-xs font-medium">
                                  {app.application_status === 'invited' ? 'Resend' : 'Send'}
                                </span>
                              </button>
                            )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {total === 0 ? 0 : (currentPage - 1) * pageSize + 1} to{' '}
              {Math.min(currentPage * pageSize, total)} of {total} applications
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                title="First page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                title="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-2 text-sm">
                Page {currentPage} of {totalPages || 1}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                title="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                title="Last page"
              >
                <ChevronsRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {drawerCurrent && (
        <BetaDetailDrawer
          app={drawerCurrent}
          busy={actionBusy}
          onClose={() => setDrawerApp(null)}
          onApprove={doApprove}
          onReject={(a) => {
            setDrawerApp(null);
            openReject(a);
          }}
          onSendLinks={doSendLinks}
        />
      )}

      {/* Reject reason modal */}
      {(rejectTarget || bulkRejecting) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-card p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                <XCircle className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  {bulkRejecting ? `Reject ${selected.size} applications` : 'Reject application'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {bulkRejecting ? 'This applies to all selected' : rejectTarget?.email}
                </p>
              </div>
            </div>
            <label className="mb-1 block text-sm font-medium">Reason (optional)</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="e.g. Outside launch market"
              className="mb-4 w-full rounded-lg border bg-background p-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setRejectTarget(null);
                  setBulkRejecting(false);
                  setRejectReason('');
                }}
                disabled={actionBusy}
                className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                disabled={actionBusy}
                className="flex items-center gap-2 rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
              >
                {actionBusy && (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                )}
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
